import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ThumbsUp, Star, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { priceParts } from "@/lib/pricing";
import { useAuth } from "@/context/AuthContext";
import NavTop from "@/components/nav/nav_top";
import ButtonGhost from "@/components/buttons/button_ghost";

// "Homes I liked": every listing this renter right-swiped or super-liked, from
// GET /api/swipes/likes. Honest empty state for real users; demo sessions with
// no backend simply show empty (no fabricated likes).
export default function LikesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [likes, setLikes] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get("/swipes/likes")
      .then((res) => alive && setLikes(Array.isArray(res.data?.likes) ? res.data.likes : []))
      .catch(() => alive && setLikes([]));
    return () => { alive = false; };
  }, [user?.id]);

  // Remove a like from this page. Optimistic (drops it immediately), rolls back
  // if the server rejects. Uses the same undo endpoint as the deck's Undo, so
  // the like is gone for good and the lister can no longer see it.
  async function unlike(e, id) {
    e.preventDefault();
    e.stopPropagation();
    const prev = likes;
    setLikes((cur) => (cur || []).filter((x) => x.id !== id));
    try {
      await api.delete(`/swipes/listing/${id}`);
      toast.success("Removed from your likes.");
    } catch {
      setLikes(prev);
      toast.error("Could not remove that like. Please try again.");
    }
  }

  return (
    <div className="min-h-screen bg-night pb-24 md:pb-10">
      <NavTop />
      <main className="mx-auto max-w-4xl px-4 pt-8 sm:px-6">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-night-text">
          <ThumbsUp size={20} className="text-brand-primary" /> Homes you liked
        </h1>
        <p className="mt-1 text-sm text-night-sub">
          Every home you swiped right on. If a lister likes you back it becomes a match.
        </p>

        {!likes ? (
          <div className="mt-16 flex justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-night-line border-t-brand-primary" />
          </div>
        ) : likes.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-night-line bg-night-surface p-10 text-center">
            <ThumbsUp className="mx-auto text-night-text" size={26} strokeWidth={1.6} />
            <h2 className="mt-3 text-lg font-semibold text-night-text">No likes yet</h2>
            <p className="mt-1 text-sm text-night-sub">Swipe right on homes you love and they show up here.</p>
            <ButtonGhost className="mt-5" onClick={() => navigate("/swipe")}>Start swiping</ButtonGhost>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 lg:grid-cols-4">
            {likes.map((l) => (
              <Link key={l.id} to={`/listing/${l.id}`} className="group">
                <div className="relative overflow-hidden rounded-2xl">
                  <img
                    src={l.photos?.[0]}
                    alt={l.title}
                    loading="lazy"
                    decoding="async"
                    className="aspect-[4/3] w-full object-cover transition group-hover:scale-[1.02]"
                  />
                  {l.super && (
                    <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-brand-primary px-2 py-0.5 text-[10px] font-bold text-white">
                      <Star size={10} className="fill-brand-ink" /> Super
                    </span>
                  )}
                  {/* Remove this like (undo). preventDefault so it never opens
                      the listing under it. */}
                  <button
                    type="button"
                    onClick={(e) => unlike(e, l.id)}
                    aria-label="Remove like"
                    className="absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/45 text-white transition hover:bg-black/65"
                  >
                    <X size={16} />
                  </button>
                </div>
                <p className="mt-2 truncate text-sm font-semibold text-night-text">{l.title}</p>
                <p className="text-xs text-night-sub">
                  ${priceParts(l).amount.toLocaleString()} {priceParts(l).unit}
                </p>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
